// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { AppName, Portal, ShowInApp, Tabs, Translation } from '@shared/ui-kit';
import { useInvestment } from './use-investment';
import {
  Allocation,
  Backtest,
  RefreshControl,
  Returns,
} from '@shared/advisory/aggregation/components';
import { AccountsSelector } from '@shared/advisory/aggregation/components/accounts-selector';
import { Positions } from './positions';

export const Investment = defineComponent({
  displayName: 'Investment',
  useSetup: useInvestment,
  View: ({
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    onRefresh,
    status,
    tabsController,
    updatedDate,
    currency,
    tags,
    tagsForSelector,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    isSyncing,
  }) => (
    <>
      <Portal container="aggregation-institutions-actions">
        <AccountsSelector
          data={accountOptions}
          onChange={onChangeAccounts}
          selectedAccounts={selectedAccounts}
          currency={currency}
          tags={tagsForSelector}
          selectedTag={selectedTag}
          onTagSelect={setSelectedTag}
          tagsByObject={tagsByObject}
        />
      </Portal>

      <div>
        <div className="mb-6 flex w-full flex-col items-stretch gap-y-3 md:flex-row md:items-center md:justify-between">
          <RefreshControl
            updatedDate={updatedDate}
            onRefresh={onRefresh}
            disabled={status === 'pending' || isSyncing}
          />
        </div>

        <Tabs.Root
          variant="underline"
          defaultValue="positions"
          baseId="investment-tabs"
          controller={tabsController}
        >
          <Tabs.Scrollable>
            <Tabs.List data-scrollable>
              <Tabs.Trigger value="positions">
                <Translation i18nKey="glossary.positions" ns="glossary" />
              </Tabs.Trigger>
              <Tabs.Trigger value="allocation">
                <Translation i18nKey="glossary.allocation" ns="glossary" />
              </Tabs.Trigger>
              <Tabs.Trigger value="returns">
                <Translation i18nKey="glossary.returns" ns="glossary" />
              </Tabs.Trigger>

              <ShowInApp apps={AppName.Advisor}>
                <Tabs.Trigger value="backtest">
                  <Translation i18nKey="common.backtest" ns="common" />
                </Tabs.Trigger>
              </ShowInApp>
            </Tabs.List>
          </Tabs.Scrollable>

          <Tabs.Content value="positions">
            <Positions />
          </Tabs.Content>

          <Tabs.Content value="returns">
            <Returns tags={tags} />
          </Tabs.Content>

          <Tabs.Content value="allocation">
            <Allocation />
          </Tabs.Content>

          <ShowInApp apps={AppName.Advisor}>
            <Tabs.Content value="backtest">
              <Backtest />
            </Tabs.Content>
          </ShowInApp>
        </Tabs.Root>
      </div>
    </>
  ),
});
